import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, Boxes, Landmark, Package, Plug, Settings, ShoppingCart, Warehouse } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CommandPalette from "@/components/CommandPalette";
import Header from "@/components/layout/Header";
import { canAccessPath, toPanelPath, withPanelBase } from "@/lib/authz";
import { OsDock } from "../shell/OsDock";
import { OsShellFrame } from "../shell/OsShellFrame";
import { OsWorkspaceTabs } from "../workspace/OsWorkspaceTabs";
import { OsText } from "../primitives";
import { upsertRouteTab } from "./tabState";
import "../obihub-os.css";

const MODULES = [
  { id: "today", label: "Bugün", path: "/panel", icon: <Boxes /> },
  { id: "sales", label: "Satışlar", path: "/panel/siparisler", icon: <ShoppingCart /> },
  { id: "products", label: "Ürünler", path: "/panel/urunler", icon: <Package /> },
  { id: "stock", label: "Stok", path: "/panel/stok", icon: <Warehouse /> },
  { id: "finance", label: "Finans", path: "/panel/finans", icon: <Landmark /> },
  { id: "integrations", label: "Entegrasyon", path: "/panel/entegrasyonlar", icon: <Plug /> },
  { id: "reports", label: "Raporlar", path: "/panel/raporlar", icon: <BarChart3 /> },
  { id: "settings", label: "Ayarlar", path: "/panel/ayarlar", icon: <Settings /> },
];

const LABELS = {
  "": "Bugün", siparisler: "Siparişler", urunler: "Ürünler", stok: "Stok & Depolar", finans: "Finans Merkezi",
  entegrasyonlar: "Entegrasyonlar", raporlar: "Raporlar", musteriler: "Cari Hesaplar", satis: "Satış Ekranı",
  satislar: "Satışlar", giderler: "Giderler", ayarlar: "Ayarlar", hesabim: "Hesabım",
};

function describePath(pathname) {
  const canonical = toPanelPath(pathname);
  const segments = canonical.replace(/^\/panel\/?/, "").split("/").filter(Boolean);
  const first = segments[0] || "";
  const last = segments[segments.length - 1] || "";
  const looksLikeId = /^[A-Za-z0-9_-]{8,64}$/.test(last) && !LABELS[last];
  const label = looksLikeId ? `${LABELS[first] || "Kayıt"} detayı` : (LABELS[last] || LABELS[first] || "Çalışma alanı");
  return { id: canonical, label, canonical };
}

export default function ObiHubOsLayout({ capability }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const current = useMemo(() => describePath(location.pathname), [location.pathname]);
  const [tabs, setTabs] = useState(() => [{ ...current, kind: "route", pinned: current.canonical === "/panel", closeable: current.canonical !== "/panel", dirty: false }]);

  useEffect(() => {
    setTabs((existing) => upsertRouteTab(existing, current));
  }, [current]);

  const visibleModules = useMemo(() => MODULES.filter((item) => canAccessPath(user, item.path)), [user]);
  const activeModule = visibleModules.find((item) => current.canonical === item.path || (item.path !== "/panel" && current.canonical.startsWith(item.path + "/")))?.id || "today";
  const go = (path) => navigate(withPanelBase(path, user, location.pathname));
  const closeTab = (id) => {
    const index = tabs.findIndex((tab) => tab.id === id);
    if (index < 0 || !tabs[index].closeable) return;
    const next = tabs.filter((tab) => tab.id !== id);
    setTabs(next);
    if (id === current.id) {
      const target = next[index - 1] || next[index] || next[0];
      if (target) go(target.canonical);
    }
  };
  const crumbs = current.canonical.replace(/^\/panel\/?/, "").split("/").filter(Boolean).map((part) => LABELS[part] || (/^[A-Za-z0-9_-]{8,64}$/.test(part) ? "Detay" : part));

  return (
    <div className="os-runtime">
      <Header onMenuClick={() => {}} />
      <OsShellFrame
      brand={<div className="flex items-center gap-3"><img src="/obihub-logo.png" alt="ObiHub" className="h-8 w-auto" /><span className="text-xs font-semibold text-slate-500">OS</span></div>}
      workspace={<OsWorkspaceTabs tabs={tabs.map((tab) => ({ ...tab, active: tab.id === current.id }))} activeId={current.id} onActivate={(id) => { const tab = tabs.find((row) => row.id === id); if (tab) go(tab.canonical); }} onCloseRequest={closeTab} onConfirmClose={() => {}} onCancelClose={() => {}} />}
      breadcrumb={<OsText variant="caption" tone="muted">{["ObiHub", ...crumbs].join(" / ")}</OsText>}
      dock={<OsDock items={visibleModules} activeId={activeModule} onNavigate={({ path }) => go(path)} commandAction={{ label: "Ne yapmak istiyorsunuz?", onInvoke: () => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true })) }} />}
      >
        <div data-os-content="true"><Outlet /></div>
        <CommandPalette />
        <span className="sr-only" data-workspace-scope-present={capability?.workspaceScope ? "true" : "false"}>ObiHub OS pilotu</span>
      </OsShellFrame>
    </div>
  );
}
